import React, { useEffect, useState } from 'react';
import { FileText, Image as ImageIcon, Film, Trash2, Download, File } from 'lucide-react';
import { Encounter } from '../../types/encounter';
import { S3Upload } from '../common/S3Upload';
import { API_ENDPOINTS } from '../../config/api';
import ConfirmDeleteModal from '../common/ConfirmDeleteModal';

interface AttachmentsSectionProps {
  encounter: Encounter;
  onUpdate: () => void;
}

interface Attachment {
  id: string;
  filename: string;
  content_type: string;
  size_bytes: number;
  url: string;
  created_at: string;
}

const AttachmentsSection: React.FC<AttachmentsSectionProps> = ({ encounter, onUpdate }) => {
  const [attachments, setAttachments] = useState<Attachment[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [fileToDelete, setFileToDelete] = useState<string | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  useEffect(() => {
    fetchAttachments();
  }, [encounter.id]);

  const fetchAttachments = async () => {
    setIsLoading(true);
    try {
      const response = await fetch(API_ENDPOINTS.ENCOUNTER_ATTACHMENTS.LIST(encounter.id), {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      if (response.ok) {
        const data = await response.json();
        setAttachments(data);
      }
    } catch (error) {
      console.error('Failed to fetch attachments:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteClick = (fileId: string) => {
    setFileToDelete(fileId);
  };

  const handleConfirmDelete = async () => {
    if (!fileToDelete) return;

    setIsDeleting(true);
    try {
      const response = await fetch(API_ENDPOINTS.ENCOUNTER_ATTACHMENTS.DELETE(encounter.id, fileToDelete), {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setAttachments(data);
        onUpdate(); // Notify parent of update
      } else {
        console.error('Failed to delete attachment');
      }
    } catch (error) {
      console.error('Error deleting attachment:', error);
    } finally {
      setIsDeleting(false);
      setFileToDelete(null);
    }
  };

  const handleUploadComplete = (updatedFiles: Attachment[]) => {
    setAttachments(updatedFiles);
    onUpdate();
  };

  const getFileIcon = (contentType: string) => {
    if (contentType.startsWith('image/')) return <ImageIcon className="w-5 h-5 text-purple-500" />;
    if (contentType.startsWith('video/')) return <Film className="w-5 h-5 text-red-500" />;
    if (contentType === 'application/pdf') return <FileText className="w-5 h-5 text-orange-500" />;
    return <File className="w-5 h-5 text-gray-500" />;
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-medium text-gray-900">Attachments</h3>
      </div>

      <S3Upload
        encounterId={encounter.id}
        onUploadComplete={(updatedFiles) => {
          // The backend returns the full list of files with signed URLs
          // But the type might not match exactly what we expect in Attachment[]
          // So we'll re-fetch to be safe and consistent
          fetchAttachments();
          onUpdate();
        }}
        className="mb-6"
      />

      {isLoading ? (
        <div className="text-center py-4 text-gray-500">Loading attachments...</div>
      ) : attachments.length === 0 ? (
        <div className="text-center py-8 bg-gray-50 rounded-lg border-2 border-dashed border-gray-200">
          <p className="text-gray-500">No attachments yet</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {attachments.map((file) => (
            <div key={file.id} className="relative group bg-white p-4 rounded-lg border border-gray-200 shadow-sm hover:shadow-md transition-shadow">
              <div className="flex items-start justify-between">
                <div className="flex items-center space-x-3">
                  {getFileIcon(file.content_type)}
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900 truncate" title={file.filename}>
                      {file.filename}
                    </p>
                    <p className="text-xs text-gray-500">
                      {formatFileSize(file.size_bytes)}
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => handleDeleteClick(file.id)}
                  className="text-gray-400 hover:text-red-500 transition-colors p-1"
                  title="Delete attachment"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>

              <div className="mt-4 flex items-center justify-end space-x-2">
                <a
                  href={file.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sm text-indigo-600 hover:text-indigo-800 font-medium flex items-center"
                >
                  <Download className="w-4 h-4 mr-1" />
                  Download
                </a>
              </div>
            </div>
          ))}
        </div>
      )}

      <ConfirmDeleteModal
        isOpen={!!fileToDelete}
        onClose={() => setFileToDelete(null)}
        onConfirm={handleConfirmDelete}
        title="Delete Attachment"
        message="Are you sure you want to delete this attachment? This action cannot be undone."
        isDeleting={isDeleting}
      />
    </div>
  );
};

export default AttachmentsSection;
